import React from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import ConfirmEmailBanner from './ConfirmEmailBanner'

// `publicView` drops the signed-in chrome for a page an anonymous visitor can
// reach. It is not the same guard as `signedIn` below and does not collapse
// into it: a signed-in user can open a shared link too, and the share page
// should still look like the public artefact it is rather than sprouting their
// account nav. The wordmark stops being a link for the same reason — /leagues
// is not a useful destination for someone who just followed a share link.
export default function Layout({ children, wide = false, publicView = false }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Shares the ['session'] key with Leagues, so this is the same cached
  // response rather than a second request. Skipped entirely on a public view,
  // where there is no session to ask about and a 401 is the expected answer.
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.session(),
    retry: false,
    enabled: !publicView,
  })

  // Whether to offer the link, not whether the powers exist — the admin API
  // checks the flag server-side on every request and refuses bearer tokens
  // outright. Hiding this would only make an admin's own nav less useful.
  const isAdmin = session.data?.user?.admin === true

  // Comes down with the session rather than from a request of its own, so the
  // banner renders on the first paint instead of appearing a moment later.
  const user = session.data?.user
  const unconfirmed = !!user && user.email_confirmed === false

  // Every item in the nav below needs a session to mean anything: "Saved
  // trades" and "Account" lead straight to a 401, and "Sign out" offers to end
  // a session that does not exist. `publicView` covered the shared-trade page,
  // where we know in advance there is nobody signed in — but the same is true
  // of a signed-out visitor on any other page, which is the first thing a new
  // user sees.
  //
  // Keyed on the user rather than on "not loading", so the nav appears once
  // there is something behind it instead of rendering and then vanishing.
  const signedIn = !!user

  // The meter, in the nav, so running out is never a surprise. A paywall that
  // appears with no warning converts badly, and someone who has watched the
  // count go down has already decided what they think about paying.
  //
  // Only fetched once there is a session to ask about; a 401 here would be
  // noise on every public page.
  const entitlement = useQuery({
    queryKey: ['billing', 'entitlement'],
    queryFn: () => api.entitlement(),
    retry: false,
    enabled: !publicView && !!session.data?.user,
  })

  const meter = entitlement.data?.entitlement
  // Deliberately silent for anyone who is entitled: a subscriber has no count
  // to watch, and a comped user has nothing to be reminded of.
  const showMeter = meter && meter.source === 'free'

  const signOut = useMutation({
    mutationFn: () => api.signOut(),
    onSuccess: () => {
      queryClient.clear()
      navigate('/leagues')
    },
  })

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-slate-800 bg-slate-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-3 py-3 sm:px-4">
          {publicView ? (
            <span className="text-sm font-semibold tracking-tight">lolcollusion</span>
          ) : (
            <Link to="/leagues" className="text-sm font-semibold tracking-tight">
              lolcollusion
            </Link>
          )}

          {!publicView && signedIn && (
            <nav className="flex items-center gap-4">
              {showMeter && (
                <NavLink
                  to="/account"
                  className={
                    meter.free_remaining === 0
                      ? 'text-xs font-medium text-amber-400 hover:text-amber-300'
                      : 'text-xs text-slate-400 hover:text-slate-200'
                  }
                  title="Free evaluations remaining this season"
                >
                  {meter.free_remaining === 0
                    ? 'No free evaluations left'
                    : `${meter.free_remaining} of ${meter.free_limit} free`}
                </NavLink>
              )}
              {/* end, or every /leagues/:id page below it would light this up
                  as well and the nav would never point anywhere useful. */}
              <NavLink
                to="/leagues"
                end
                className={({ isActive }) =>
                  `text-xs ${
                    isActive
                      ? 'font-medium text-slate-100'
                      : 'text-slate-400 hover:text-slate-200'
                  }`
                }
              >
                Leagues
              </NavLink>

              <NavLink
                to="/trades"
                className={({ isActive }) =>
                  `text-xs ${
                    isActive
                      ? 'font-medium text-slate-100'
                      : 'text-slate-400 hover:text-slate-200'
                  }`
                }
              >
                Saved trades
              </NavLink>

              {/* The meter above also points here, but it is a status readout
                  that disappears the moment someone subscribes. This is the
                  navigation, and it has to be there for everyone: it is where
                  a password is changed and a Sleeper account is relinked. */}
              <NavLink
                to="/account"
                className={({ isActive }) =>
                  `text-xs ${
                    isActive
                      ? 'font-medium text-slate-100'
                      : 'text-slate-400 hover:text-slate-200'
                  }`
                }
              >
                Account
              </NavLink>

              {isAdmin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `text-xs ${
                      isActive
                        ? 'font-medium text-slate-100'
                        : 'text-slate-400 hover:text-slate-200'
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}

              <button
                type="button"
                onClick={() => signOut.mutate()}
                className="text-xs text-slate-400 hover:text-slate-200"
              >
                Sign out
              </button>
            </nav>
          )}
        </div>
      </header>

      {unconfirmed && <ConfirmEmailBanner email={user.email_address} />}

      <main className={`mx-auto px-3 py-5 sm:px-4 sm:py-6 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>
        {children}
      </main>
    </div>
  )
}
